import type { User, UserRole } from '../types';

/** Patriotic palette — red, white, blue, gold. */
export const JULY_FOURTH_COLORS = ['#B22234', '#FFFFFF', '#3C3B6E', '#FFD700', '#0047AB', '#DC143C'];

/** Event id stored on the user doc — one show per employee per calendar year. */
export function julyFourthEventId(date = new Date()): string {
  return `july4-${date.getFullYear()}`;
}

/** Show July 3–5 (local calendar). */
export function isJulyFourthSeason(date = new Date()): boolean {
  const month = date.getMonth(); // 0-indexed; July = 6
  const day = date.getDate();
  return month === 6 && day >= 3 && day <= 5;
}

export function isEmployeeCelebrationUser(role: UserRole | undefined): boolean {
  return role === 'employee' || role === 'admin' || role === 'staff';
}

const PREVIEW_SESSION_KEY = 'lctfJulyFourthPreview';

/** Dev/preview: `?lctfJulyFourthPreview=1` replays without clearing Firestore (survives `/` → `/leads` redirect). */
export function isJulyFourthPreviewMode(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('lctfJulyFourthPreview') === '1') {
    try {
      sessionStorage.setItem(PREVIEW_SESSION_KEY, '1');
    } catch {
      /* ignore */
    }
    return true;
  }
  try {
    return sessionStorage.getItem(PREVIEW_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearJulyFourthPreviewMode(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(PREVIEW_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export type JulyFourthBlockReason =
  | 'ok'
  | 'no-user'
  | 'wrong-role'
  | 'out-of-season'
  | 'already-seen';

export function getJulyFourthBlockReason(
  user: User | null,
  date = new Date(),
): JulyFourthBlockReason {
  if (!user) return 'no-user';
  if (!isEmployeeCelebrationUser(user.role)) return 'wrong-role';
  if (!isJulyFourthSeason(date)) return 'out-of-season';
  const eventId = julyFourthEventId(date);
  if (user.seenCelebrationIds?.includes(eventId)) return 'already-seen';
  return 'ok';
}

/** Localhost dev: replay during the season without clearing Firestore. */
export function isJulyFourthLocalDevReplay(): boolean {
  if (process.env.NODE_ENV !== 'development') return false;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

export function shouldSkipCelebrationPersistence(): boolean {
  return isJulyFourthPreviewMode() || isJulyFourthLocalDevReplay();
}

export function shouldShowJulyFourthCelebration(user: User | null, date = new Date()): boolean {
  if (!user || !isEmployeeCelebrationUser(user.role)) return false;
  if (!isJulyFourthSeason(date)) return false;
  if (isJulyFourthPreviewMode() || isJulyFourthLocalDevReplay()) return true;
  const eventId = julyFourthEventId(date);
  if (user.seenCelebrationIds?.includes(eventId)) return false;
  return true;
}
